import {camelize, camelizeKeys} from 'humps'
/**
 * useErrorTranslate Hook
 *
 * A custom React hook for translating API error responses into user-friendly messages.
 *
 * @example
 * Basic usage:
 * ```jsx
 * import { useTranslation } from 'react-i18next'
 * import useErrorTranslate from './hooks/useErrorTranslate'
 *
 * const MyComponent = () => {
 *   const { t } = useTranslation('logoUpload')
 *
 *   const onErrorsTranslate = useErrorTranslate(t, 'errors')
 *
 *   const handleApiResponse = (response) => {
 *     if (!response.success) {
 *       const errorMessages = onErrorsTranslate(response.errors)
 *       // errorMessages is an array of translated strings
 *       // Translation keys are built as 'errors.dimensionMinInclusion'
 *       // with fallback to 'errors.defaultValue' if translation not found
 *       toast.error(errorMessages.join(', '))
 *     }
 *   }
 * }
 * ```
 *
 * @example
 * Error response format:
 * ```json
 * {
 *   "success": false,
 *   "errors": {
 *     "logo": [
 *       {
 *         "error": "dimension_min_inclusion",
 *         "min": "400..160",
 *         "validator_type": "dimension",
 *         "filename": "",
 *         "width": 400,
 *         "height": 160
 *       }
 *     ]
 *   }
 * }
 * ```
 *
 * @param {Function} t - Translation function from react-i18next
 * @param {string} path - Base path for translation keys (e.g., 'errors')
 * @returns {Function} Function that translates error responses
 */

const onErrorTranslate = (t, key, {error, ...rest}) => {
  return t(`${key}.${camelize(error)}`, {
    defaultValue: t(`${key}.defaultValue`, {
      defaultValue: 'An error occurred',
    }),
    ...camelizeKeys(rest),
  })
}

const onErrorsTranslate = (t, key, responseErrors) => {
  const originalArray = Object.values(responseErrors).reduce((acc, errors) => {
    return [...acc, ...errors.map((error) => onErrorTranslate(t, key, error))]
  }, [])

  const uniqueSet = new Set(originalArray)

  return [...uniqueSet]
}

const useErrorTranslate = (t, path) => {
  return (responseErrors) => {
    return onErrorsTranslate(t, path, responseErrors)
  }
}

export default useErrorTranslate
